import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { getValidToken } from "../../../utils/authUtils";
import {
  formatCurrency,
  formatDate,
  formatText,
  formatTime12Hour,
  formatVehicleNumber,
} from "../../../utils/utils";
import { useReportFilter } from "../context/ReportFilterContext";
import { tableCellStyles, tableHeaderStyles } from "../../../utils/styles";
import { useNavigate } from "react-router-dom";
import { Loading } from "../../../components/Loading";

interface RentalJourneyEntry {
  "Rental Journey ID": string;
  "Vehicle Number": string;
  "Vehicle Display Name": string;
  "Vehicle Type": string;
  "Owner Name": string;
  "Owner Contact": string;
  "Party Name": string;
  "Party Contact": string;
  "Company Name": string;
  "Journey Start Date": string;
  "Journey End Date": string | null;
  "Start Location": string;
  "End Location": string;
  "Odometer Reading": number;
  Remarks: string | null;
  "Fixed Income Amount": string;
  "Per KM Income Amount": string;
  "Other Income Amount": string;
  "Total Income Expected": string;
  "Total Income Received": string;
  "Income Balance": string;
  "Fuel Expense": string;
  "Maintenance Expense": string;
  "Toll Expense": string;
  "Parking Expense": string;
  "Staff Expense": string;
  "Challan Expense": string;
  "Other Expense": string;
  "Total Expense": string;
  "Expense Advance Paid": string;
  "Expense Settlement Paid": string;
  "Total Expense Paid": string;
  "Expense Balance": string;
  "Net Profit/Loss": string;
  "Net Cash Flow": string;
  "Net Outstanding": string;
  "Vehicle Type Category": string;
  "Created At": string;
}

interface RentalJourneySummary {
  totalJourneys: number;
  totalIncomeExpected: string;
  totalIncomeReceived: string;
  totalIncomePending: string;
  fixedIncomeAmount: string;
  perKmIncomeAmount: string;
  totalExpenseAmount: string;
  totalExpensePaid: string;
  totalExpensePending: string;
  fuelExpenseAmount: string;
  maintenanceExpenseAmount: string;
  tollExpenseAmount: string;
  parkingExpenseAmount: string;
  staffExpenseAmount: string;
  challanExpenseAmount: string;
  otherExpenseAmount: string;
  netProfitLoss: string;
  profitableJourneys: number;
  lossJourneys: number;
  breakEvenJourneys: number;
  ownedVehicleJourneys: number;
  marketLoadJourneys: number;
  journeysWithIncomeBalance: number;
  journeysWithExpenseBalance: number;
  fullyPaidJourneys: number;
  incomeCollectionEfficiency: string;
  expensePaymentRatio: string;
}

interface RentalReportInfo {
  companyId: string;
  startDate: string;
  endDate: string;
  totalRecords: number;
  generatedAt: string;
  filters: {
    vehicleIds: string[] | null;
    partyIds: string[] | null;
    marketLoadIds: string[] | null;
    reportRange: string;
  };
}

interface RentalLedgerReportData {
  reportInfo: RentalReportInfo;
  summary: RentalJourneySummary;
  ledgerData: RentalJourneyEntry[];
}

const RentalLedgerTableRow: React.FC<{ entry: RentalJourneyEntry }> = ({
  entry,
}) => {
  const navigate = useNavigate();

  return (
    <TableRow hover>
      <TableCell sx={{ ...tableCellStyles }}>
        <Tooltip title="View Journey">
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.75rem",
              cursor: "pointer",
            }}
            onClick={() => {
              // Navigate to rental journey details - adjust path as needed
              navigate(`/rental/${entry["Rental Journey ID"]}/logs`);
            }}
          >
            <span
              style={{
                textDecoration: "underline",
                color: "#1976d2",
              }}
            >
              {formatDate(entry["Journey Start Date"])}
            </span>
            <br />
            <span
              style={{
                fontSize: "10px",
                color: "#666",
                textDecoration: "none",
              }}
            >
              {formatTime12Hour(entry["Journey Start Date"])}
            </span>
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ ...tableCellStyles }}>
        <Typography variant="caption">{entry["Start Location"]}</Typography>
      </TableCell>
      <TableCell sx={{ ...tableCellStyles }}>
        <Typography variant="caption">
          {entry["Journey End Date"]
            ? formatDate(entry["Journey End Date"])
            : "-"}
          <br />
          <span
            style={{
              fontSize: "10px",
              color: "#666",
            }}
          >
            {entry["Journey End Date"]
              ? formatTime12Hour(entry["Journey End Date"])
              : ""}
          </span>
        </Typography>
      </TableCell>
      <TableCell sx={{ ...tableCellStyles }}>
        <Typography variant="caption">{entry["End Location"]}</Typography>
      </TableCell>
      <TableCell sx={{ ...tableCellStyles }}>
        <Typography variant="caption">
          {formatVehicleNumber(entry["Vehicle Number"])}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...tableCellStyles }}>
        <Typography variant="caption">{entry["Party Name"]}</Typography>
      </TableCell>
      <TableCell sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}>
        <Typography variant="caption">
          {formatText(entry["Vehicle Type Category"])}
        </Typography>
      </TableCell>

      {/* Income Section */}
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Fixed Income Amount"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Per KM Income Amount"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Other Income Amount"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography
          variant="caption"
          sx={{ fontSize: "10px", fontWeight: 500 }}
        >
          {formatCurrency(parseFloat(entry["Total Income Expected"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Total Income Received"]))}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}
      >
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Income Balance"]))}
        </Typography>
      </TableCell>

      {/* Expense Section */}
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Fuel Expense"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Maintenance Expense"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Toll Expense"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Parking Expense"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Staff Expense"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Challan Expense"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Other Expense"]))}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}
      >
        <Typography
          variant="caption"
          sx={{ fontSize: "10px", fontWeight: 500 }}
        >
          {formatCurrency(parseFloat(entry["Total Expense"]))}
        </Typography>
      </TableCell>

      {/* Payment Section */}
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Total Expense Paid"]))}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}
      >
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Expense Balance"]))}
        </Typography>
      </TableCell>

      {/* P&L Column - Sticky */}
      <TableCell
        align="right"
        sx={{
          minWidth: 100,
          position: "sticky",
          right: 0,
          bgcolor: "white",
          zIndex: 2,
          borderLeft: "1px solid #e0e0e0",
          boxShadow: "-1px 0 4px rgba(0,0,0,0.1)",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.75rem",
            fontWeight: 600,
            color:
              parseFloat(entry["Net Profit/Loss"]) >= 0
                ? "success.main"
                : "error.main",
          }}
        >
          {formatCurrency(parseFloat(entry["Net Profit/Loss"]))}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

const RentalLedgerReport: React.FC = () => {
  const { filterState } = useReportFilter();
  const [displayedRows, setDisplayedRows] = useState(20);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const {
    timeFilter,
    customStartDate,
    customEndDate,
    selectedVehicleIds,
    selectedMarketLoadIds,
    selectedParties,
  } = filterState;

  // Reset displayed rows when filters change
  useEffect(() => {
    setDisplayedRows(20);
  }, [
    timeFilter,
    customStartDate,
    customEndDate,
    selectedVehicleIds,
    selectedMarketLoadIds,
    selectedParties,
  ]);

  const {
    data: reportData,
    isLoading,
    isFetching,
    isError,
  } = useQuery<{
    data: {
      data: RentalLedgerReportData;
    };
    isSuccess: boolean;
    message: string;
  }>({
    queryKey: [
      "rentalLedgerReport",
      timeFilter,
      customStartDate,
      customEndDate,
      selectedVehicleIds,
      selectedMarketLoadIds,
      selectedParties,
    ],
    queryFn: async () => {
      const token = await getValidToken();
      let url = `${process.env.REACT_APP_API_HOST}reports/rental-journey-ledger/${timeFilter}`;

      if (timeFilter === "custom") {
        url = `${process.env.REACT_APP_API_HOST}reports/rental-journey-ledger/custom`;
      }

      const params: any = {};
      if (timeFilter === "custom") {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }

      if (selectedVehicleIds && selectedVehicleIds.length > 0) {
        params.vehicleIds = selectedVehicleIds;
      }
      if (selectedMarketLoadIds && selectedMarketLoadIds.length > 0) {
        params.marketLoadIds = selectedMarketLoadIds;
      }
      if (selectedParties && selectedParties.length > 0) {
        params.partyIds = selectedParties;
      }

      const response = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    enabled: timeFilter !== "custom" || (!!customStartDate && !!customEndDate),
  });

  const handleShowMore = () => {
    setIsLoadingMore(true);
    // Simulate loading delay
    setTimeout(() => {
      setDisplayedRows((prev) => prev + 20);
      setIsLoadingMore(false);
    }, 500);
  };

  const { ledgerData, reportInfo } = reportData?.data?.data ?? {
    ledgerData: [],
    reportInfo: { companyId: "" },
  };

  const displayedData = ledgerData?.slice(0, displayedRows);
  const hasMoreData = displayedRows < ledgerData?.length;

  return (
    <Box sx={{ position: "relative" }}>
      {/* Booking Data Table */}
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <Typography
          variant="h6"
          sx={{ fontSize: "0.9rem", fontWeight: 500, p: 1.5 }}
        >
          Booking Ledger Report
        </Typography>

        {isLoading && (
          <Loading fullScreen={false} message="Loading booking report..." />
        )}
        {isFetching && !isLoading && (
          <Loading fullScreen={false} message="Refreshing booking report..." />
        )}
        {ledgerData.length === 0 ? (
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.75rem",
              color: "text.secondary",
              px: 1.5,
              pb: 2,
            }}
          >
            No booking data available
          </Typography>
        ) : (
          <>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.75rem",
                color: "text.secondary",
                px: 1.5,
                pb: 1,
              }}
            >
              Showing {displayedData.length} of {ledgerData.length} bookings
            </Typography>
            <TableContainer
              sx={{
                "&::-webkit-scrollbar": {
                  height: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f1f1",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#c1c1c1",
                  borderRadius: "3px",
                  "&:hover": {
                    backgroundColor: "#a8a8a8",
                  },
                },
                "&::-webkit-scrollbar-corner": {
                  backgroundColor: "#f1f1f1",
                },
              }}
            >
              <Table
                stickyHeader
                size="small"
                aria-label="booking report table"
                sx={{
                  tableLayout: "auto",
                  "& .MuiTableCell-root": {
                    padding: "4px 8px",
                    fontSize: "0.75rem",
                  },
                }}
              >
                <TableHead>
                  {/* First header row - Group headers */}
                  <TableRow>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        minWidth: 120,
                      }}
                    >
                      Booking Start
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        minWidth: 120,
                      }}
                    >
                      From Location
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        minWidth: 120,
                      }}
                    >
                      Booking End
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        minWidth: 120,
                      }}
                    >
                      To Location
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        minWidth: 120,
                      }}
                    >
                      Vehicle
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        minWidth: 120,
                      }}
                    >
                      Party Name
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        minWidth: 100,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Vehicle Type
                    </TableCell>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Income Details
                    </TableCell>
                    <TableCell
                      colSpan={8}
                      align="center"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Expense Details
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      align="center"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Expense Payment Details
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        position: "sticky",
                        right: 0,
                        bgcolor: "#f5f5f5",
                        zIndex: 3,
                        borderLeft: "1px solid #e0e0e0",
                        boxShadow: "-1px 0 4px rgba(0,0,0,0.1)",
                        minWidth: 120,
                        textAlign: "center",
                      }}
                    >
                      Net P&L
                    </TableCell>
                  </TableRow>

                  {/* Second header row - Sub headers */}
                  <TableRow>
                    {/* Income Details */}
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Fixed
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Per KM
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Other
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                      }}
                    >
                      Total
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                      }}
                    >
                      Received
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Balance
                    </TableCell>

                    {/* Expense Details */}
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 80,
                      }}
                    >
                      Fuel
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Maintenance
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 70,
                      }}
                    >
                      Toll
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 80,
                      }}
                    >
                      Parking
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 70,
                      }}
                    >
                      Staff
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 70,
                      }}
                    >
                      Challan
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 80,
                      }}
                    >
                      Other
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Total
                    </TableCell>

                    {/* Payment Details */}
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Total Paid
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Balance
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedData.map((entry, index) => (
                    <RentalLedgerTableRow
                      key={`${entry["Rental Journey ID"]}-${index}`}
                      entry={entry}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {hasMoreData && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
                <Typography
                  variant="caption"
                  onClick={handleShowMore}
                  sx={{
                    fontSize: "0.75rem",
                    cursor: "pointer",
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    fontWeight: 500,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  {isLoadingMore ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Loading more...
                    </>
                  ) : (
                    `Show More (${ledgerData.length - displayedRows} remaining)`
                  )}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default RentalLedgerReport;
